<script lang="ts" setup>
import DeadheadStop from "@/views/trips/components/DeadheadStop.vue";
import type { RouteStopDto, TripDto, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { computed } from "vue";
import { useTrip } from "@/composables";
import { RouteStatus, RouteStopStatus } from "@/data/trip";
import RouteStop from "@/views/trips/components/RouteStop.vue";

const { getLoadedMiles, createEmptyRouteStop } = useTrip();

const props = defineProps<{
  route: TripRouteDto;
  trip: TripDto;
}>();

const emits = defineEmits<{
  (e: "change"): void;
}>();

const emptyMiles = computed(() => {
  return props.route.emptyMiles || 0;
});

const loadedMiles = computed(() => {
  return props.route.distances ? getLoadedMiles(props.route.distances) : 0;
});

const totalDistance = computed(() => {
  return emptyMiles.value + loadedMiles.value;
});

const lastActionPerformed = computed(() => {
  return props.route.routeStops.findLast(
    (s) => s.status !== RouteStopStatus.notStarted && s.status !== RouteStopStatus.inRoute,
  );
});

const canUndoDeadhead = computed(() => {
  return props.route.dispatchConfirmed && !lastActionPerformed.value && props.route.status !== RouteStatus.cancelled;
});

const routeStops = computed<RouteStopDto[]>(() => {
  const allPositions = new Set<number>();

  for (const route of props.trip.routes) {
    for (const stop of route.routeStops) {
      allPositions.add(stop.position);
    }
  }
  const sortedPositions = Array.from(allPositions).sort((a, b) => a - b);

  const currentStopsMap = new Map(props.route.routeStops.map((stop) => [stop.position, stop]));

  return sortedPositions.map((position) => {
    const existedStop = currentStopsMap.get(position);

    if (existedStop) {
      return existedStop;
    }

    const emptyRouteStop = createEmptyRouteStop();
    emptyRouteStop.position = position;

    return emptyRouteStop;
  });
});

function canUndoStop(stop: RouteStopDto | undefined) {
  if (!stop || !lastActionPerformed.value) {
    return false;
  }

  return lastActionPerformed.value.id === stop.id;
}

function getActiveStopIndex(stop: RouteStopDto) {
  const activeStops = routeStops.value.filter((routeStop) => routeStop.id);

  return activeStops.findIndex((activeStop) => activeStop.id === stop.id) + 2;
}
</script>

<template>
  <div class="rounded bg-white pa-4 mb-4">
    <div class="font-weight-500 text-grey-darken-2 mb-3">Route stops</div>
    <deadhead-stop :route="route" :trip="trip" :undoable="canUndoDeadhead" @change="emits('change')"></deadhead-stop>
    <template v-for="routeStop in routeStops">
      <route-stop
        v-if="routeStop"
        :key="routeStop.id"
        :index="routeStop.id ? getActiveStopIndex(routeStop) : null"
        :route="route"
        :stop="routeStop"
        :trip="trip"
        :undoable="canUndoStop(routeStop)"
        @change="emits('change')"
      ></route-stop>
    </template>
    <div class="text-grey-darken-1 font-size-12 mt-1">
      <div v-if="emptyMiles" class="text-right">
        Empty miles: <span class="font-weight-500">{{ $filters.miles(emptyMiles) }} miles</span>
      </div>
      <div class="text-right">
        Loaded miles: <span class="font-weight-500">{{ $filters.miles(loadedMiles) }} miles</span>
      </div>
      <div class="text-right">
        Total distance: <span class="font-weight-500">{{ $filters.miles(totalDistance) }} miles</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
